import clone from 'clone'
import i18next from 'i18next'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { Box, IconButton, SvgIconTypeMap, TextField, Typography } from '@mui/material'
import { AppContextProps } from '../../hooks/useApp'
import { Device, ID, Port, TimeString } from '../../types/models'
import { PortConfig, PortType } from '../../types/ports'
import { PortControl } from '../ports/PortControl'
import { Edit, QuestionMark, Visibility, VisibilityOff } from '@mui/icons-material'
import { now } from '../../utils/time'
import { DateTime } from 'luxon'
import { Appliance, ApplianceData, ConfigurationViewProps } from '../appliances/Appliance'
import { getAppliance } from '../appliances'
import { SystemControl } from '../SystemControl'
import { isDebug } from '../../utils/misc'
import { Trans } from 'react-i18next'
import { SocketMessage } from '../../types/messaging'
import { callAxios } from '../../hooks/useAxios'

export type WidgetType =
  'BATTERY_STATE' |
  'COMSUMPTION_STAT' |
  'PRODUCTION_STAT' |
  'POWER_GRAPH' |
  'PRICE_GRAPH' |
  'RELAY_BAR' |
  'PRICE_STAT' |
  'TEMPERATURE_STAT' |
  'HEAT_PUMP_STATE'

export interface BatteryStateWidgetConfig {
}

export interface ConsumptionStatWidgetConfig {
}

export interface ProductionStatWidgetConfig {
}

export interface PowerGraphWidgetConfig {
}

export interface PriceGraphWidgetConfig {
}

export interface RelayBarWidgetConfig {
}

export interface PriceStatWidgetConfig {
}

export interface TemperatureStatWidgetConfig {
}

export interface HeatPumpStateWidgetConfig {
}

export type WidgetConfig =
  BatteryStateWidgetConfig |
  ConsumptionStatWidgetConfig |
  ProductionStatWidgetConfig |
  PowerGraphWidgetConfig |
  PriceGraphWidgetConfig |
  RelayBarWidgetConfig |
  PriceStatWidgetConfig |
  HeatPumpStateWidgetConfig

export interface WidgetData {
  id?: ID
  location_id?: ID
  name: string
  type: WidgetType
  visible?: boolean
  position?: number
  appliances: Appliance[] | ApplianceData[] | ID[]
  config: WidgetConfig
}

export interface DashboardViewProps {
  upgrading?: boolean
  refresh: () => void
  testing?: boolean
}

export interface TestViewProps {
}

/**
 * Screen widget.
 */
export class Widget<DataType={}, ConfigType extends WidgetConfig=WidgetConfig> {
  id?: ID
  name: string
  type: WidgetType
  visible?: boolean
  position?: number
  control: SystemControl
  appliances: Appliance[]
  config: ConfigType

  data?: DataType

  app: AppContextProps

  constructor(data: WidgetData, control: SystemControl, app: AppContextProps) {
    this.id = data.id
    this.name = data.name
    this.type = data.type
    this.control = control
    this.visible = data.visible
    this.position = data.position
    this.appliances = []
    if (data.appliances.length) {
      if (data.appliances[0] instanceof Appliance) {
        this.appliances = data.appliances as Appliance[]
      } else if (typeof data.appliances[0] === 'number') {
        // Fill them in later manually if needed.
      } else {
        this.appliances = data.appliances.map(a => getAppliance(a as ApplianceData, control, app))
      }
    }
    this.config = clone(data.config as ConfigType)

    this.app = app
  }

  toJSON(): Record<string, unknown> {
    const ret = {
      name: this.name,
      type: this.type,
      visible: this.visible,
      position: this.position,
      appliances: this.appliances.map(a => a.id),
      config: clone(this.config)
    }
    return this.id ? { id: this.id, ...ret } : ret
  }

  /**
   * Set up controls for ports.
   */
  connectControls(control: SystemControl): void {
    for (const appliance of this.appliances) {
      appliance.connectControls(control)
    }
  }

  /**
   * List all controls.
   */
  get controls(): PortControl<Port, PortConfig>[] {
    let ret: PortControl<Port, PortConfig>[] = []
    for (const appliance of this.appliances) {
      ret = ret.concat(appliance.controls)
    }
    return ret
  }

  /**
   * Get the primary appliance, i.e. the first appliance.
   */
  get appliance(): Appliance {
    if (this.appliances.length === 0) {
      throw new Error(`Cannot access appliances when there is none in '${this.getName()}' widget.`)
    }
    return this.appliances[0]
  }

  /**
   * Get the primary port control, i.e. the first port of the first appliance.
   */
  get portControl(): PortControl<Port, PortConfig> {
    if (this.appliances.length === 0) {
      throw new Error(`Cannot access appliances when there is none in '${this.getName()}' widget.`)
    }
    if (this.appliances[0].controls.length === 0) {
      throw new Error(`Cannot access ports when there is none in '${this.appliances[0].getName()}' appliance.`)
    }
    return this.appliances[0].controls[0]
  }

  /**
   * List all ports.
   */
  get ports(): Port[] {
    const ret: Port[] = []
    for (const ctrl of this.controls) {
      ret.push(ctrl.port)
    }
    return ret
  }

  /**
   * Get the promary device if it exists.
   */
  get device(): Device | null {
    if (this.appliances.length > 0 && this.appliances[0].controls.length > 0) {
      return this.appliances[0].controls[0].device
    }
    return null
  }

  /**
   * Check if this widget relates to port with the given ID.
   * @param id
   */
  hasPortId(id: ID) {
    for (const ctrl of this.controls) {
      if (ctrl.port.id === id) {
        return true
      }
    }
    return false
  }

  /**
   * Collect and translate all errors in ports.
   */
  errorFlags(): string[] {
    const errors: Set<string> = new Set<string>(this.controls.map(ctrl => ctrl.errorMessages()).reduce((prev, cur) => prev.concat(cur)))

    if (this.device?.values?.online === false) {
      errors.add(this.app.t('Error-TIMEOUT'))
    }

    return [...errors]
  }

  /**
   * A mapping of URLs that we want for data to be fetched.
   */
  dataUrls(): Record<string, string> {
    return {}
  }

  /**
   * Slots needed in the 4-column grid.
   */
  get width(): number {
    return 1
  }

  /**
   * Multiples of base row needed.
   */
  get height(): number {
    return 1
  }

  /**
   * Visualization hints.
   */
  get textColor(): string {
    return 'primary.contrastText'
  }

  get bgColor(): string {
    return 'background.default'
  }

  get Icon(): OverridableComponent<SvgIconTypeMap<{}, 'svg'>> {
    return QuestionMark
  }

  get borderRadius(): string {
    return '0.5rem'
  }

  /**
   * Check if the appliance uses device.
   */
  isGeneric(): boolean {
    return this.appliances.length === 0
  }

  /**
   * Check if the device is upgrading.
   */
  isUpgrading(): boolean {
    return this.appliances.length ? this.portControl.deviceControl.isUpgrading() : false
  }

  /**
   * Either get the name of this widget if given or otherwise the first appliance name.
   */
  getName(): string {
    if (this.name) return this.name
    return this.appliances.length ? this.appliances[0].getName() : ''
  }

  /**
   * The name the user gave this widget, or undefined when there is none. Locations created before
   * the server stopped naming default widgets have the default title stored as the name, translated
   * to the language of the account's creator (e.g. "Pörssihinnat"); such a name counts as no name,
   * so that the title follows the UI language.
   */
  customName(defaultTitle: string): string | undefined {
    if (!this.name) return undefined
    const languages = Object.keys(i18next.options?.resources ?? {})
    const defaults = [defaultTitle, ...languages.map(lng => i18next.t(defaultTitle, { lng }))]
    return defaults.includes(this.name) ? undefined : this.name
  }

  /**
   * Find port controls matching the filter.
   */
  findControslByFilter(fn: (ctrl: PortControl<Port, PortConfig>) => boolean): PortControl<Port, PortConfig>[] {
    let match: PortControl<Port, PortConfig>[] = []
    for (const app of this.appliances) {
      match = match.concat(app.findControslByFilter(fn))
    }
    return match
  }

  /**
   * Verify that we have exactly one port of the given type and return it.
   */
  findOneControl(type: PortType, alt: PortType | undefined = undefined): PortControl<Port, PortConfig> {
    let match = this.findControslByFilter(ctrl => ctrl.port.type === type)
    if (match.length === 0 && alt !== undefined) {
      match = this.findControslByFilter(ctrl => ctrl.port.type === alt)
    }
    if (match.length === 0) {
      throw new Error(`Cannot find any ${type} ports from the list of controls.`)
    }
    if (match.length > 1) {
      throw new Error(`Found too many ${type} ports from the list of controls.`)
    }
    return match[0]
  }

  /**
   * Get the time range for the dashboard visuals.
   */
  dateRange(): [TimeString, TimeString] {
    const currentTime = now()
    const from: TimeString = DateTime.local(currentTime.year, currentTime.month, currentTime.day).toUTC().toISO() as TimeString
    const to: TimeString = DateTime.local(currentTime.year, currentTime.month, currentTime.day, 23, 0, 0).plus({ hours: 1 }).toUTC().toISO() as TimeString
    return [from, to]
  }

  /**
   * Rendering the widget for home page.
   */
  dashboardView(props: DashboardViewProps): React.ReactElement {

    return <Box key={this.id} sx={{
      gridColumnEnd: `span ${this.width}`,
      gridRowEnd: `span ${this.height}`,
      borderRadius: this.borderRadius,
      backgroundColor: this.bgColor,
    }}>
      {this.draw(props)}
    </Box>
  }

  /**
   * Rendering the widget.
   */
  draw(props: DashboardViewProps): React.ReactElement {
    throw new Error(`A widget ${this.type} does not implement draw().`)
  }

  /**
   * Optional title for individual widget.
   */
  dashboardTitle(firstOfKind: boolean): string | undefined {
    return undefined
  }

  /**
   * Rendering the widget configuration.
   */
  configurationView(props: ConfigurationViewProps): React.ReactElement {
    return <Box key={this.id} sx={{
      borderWidth: '1px',
      borderStyle: 'dotted',
      borderColor: 'primary.main',
      gridColumnEnd: `span ${this.width}`,
      gridRowEnd: `span ${this.height}`,
      borderRadius: '0.5rem',
      backgroundColor: this.bgColor,
      fontWeight: 'bold',
      pl: 1,
      '&:hover': {
        opacity: 1.0
      }
    }}>
      {this.drawConfig(props)}
    </Box>
  }

  /**
   * Rendering the layout for widget configuration.
   */
  drawConfig(props: ConfigurationViewProps): React.ReactElement {
    const Icon = this.Icon
    return <Box sx={{
      color: this.textColor,
    }}>
      <Box>
        {this.editableTitle(props)}
        <Box sx={{
          display: 'flex'
        }}>
          <Icon sx={{ height: `${2 * this.height - 1}rem`, width: `${2 * this.height - 1}rem` }}/>
          {this.configText(props)}
        </Box>
      </Box>
    </Box>
  }

  /**
   * Draw title with edit contols.
   */
  editableTitle(props: ConfigurationViewProps): React.ReactElement {
    const { edit, onClickEdit, onChangeName, onSave, onClickVisibility } = props
    return (
      <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'auto 2.2rem 2.5rem' }}>
        {!edit &&
          <Box>{this.getName()}</Box>
        }
        {edit &&
          <TextField
            sx={{ colot: this.textColor }}
            size="small"
            autoFocus
            value={this.getName()}
            onChange={(e) => onChangeName(e.target.value) }
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                onSave()
              }
            }}
            onBlur={() => onClickEdit()}
          />
        }
        <Box sx={{ mt: -1 }}>
          {!edit &&
            <IconButton sx={{ color: this.textColor }} onClick={onClickEdit}>
              <Edit/>
            </IconButton>
          }
        </Box>
        <Box sx={{ mt: -1 }}>
          <IconButton sx={{ color: this.textColor }} onClick={onClickVisibility}>
            {this.visible ? <VisibilityOff/> : <Visibility/>}
          </IconButton>
        </Box>
      </Box>
    )
  }

  configText(props: ConfigurationViewProps): React.ReactElement {
    const debug = isDebug('port-numbers') ? this.controls.map(ctrl => `${ctrl.device.hw_id} ${ctrl.port.port_number}`).join(' ') : ''
    return <Box>
      {debug}
    </Box>
  }

  drawTestView(props: TestViewProps) {
    return <Typography className="big-initial" sx={{ fontSize: '3rem', color: 'white' }}><Trans>Connected</Trans></Typography>
  }

  getTestingInfo(): React.ReactElement | undefined {
    return undefined
  }

  /**
   * Fetch and fill in remote data.
   */
  async fetchData() {
    const results: Record<string, unknown> = {}
    for (const [name, url] of Object.entries(this.dataUrls())) {
      const out = await callAxios({ url })
      if (out.success) {
        results[name] = out.data
      } else {
        this.app.addError(out)
      }
    }
    this.data = results as DataType
  }

  /**
   * If this returns true, the widget will be refreshed on certain message.
   * @param msg
   * @returns
   */
  needRefreshOnMessage(msg: SocketMessage): boolean {
    return false
  }

  /**
   * A hook called after new data loaded but before refresh is executed.
   */
  async beforeRefresh(): Promise<void> {
  }

  /**
   * A hook called when drawed first time.
   */
  enter(): void {
  }

  /**
   * Exit hook when the widget is leaving the screen.
   */
  exit(): void {
  }
}
